import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LuFlag, LuPlus } from "react-icons/lu";
import { api, ApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";

export function FeatureFlagsPage() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["console", "admin", "flags"], queryFn: () => api.adminFeatureFlags.list() });
  const update = useMutation({
    mutationFn: ({ name, body }: { name: string; body: { enabled?: boolean; description?: string } }) =>
      api.adminFeatureFlags.update(name, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["console", "admin", "flags"] }),
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Failed."),
  });
  const create = useMutation({
    mutationFn: (body: { name: string; description?: string }) => api.adminFeatureFlags.create(body),
    onSuccess: () => {
      toast.success("Flag created.");
      setNewFlag({ name: "", description: "" });
      qc.invalidateQueries({ queryKey: ["console", "admin", "flags"] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Failed."),
  });
  const [newFlag, setNewFlag] = useState({ name: "", description: "" });

  return (
    <>
      <PageHeader
        icon={LuFlag}
        title="Feature flags"
        description="Toggle features without redeploying."
      />
      <div className="p-8 space-y-6">
        <Card>
          <div className="px-5 py-3 border-b border-[var(--color-border)] flex items-center gap-2">
            <LuPlus className="w-4 h-4 text-[var(--color-text-subtle)]" />
            <p className="text-sm font-semibold text-[var(--color-text)]">New flag</p>
          </div>
          <div className="p-5 flex flex-wrap items-end gap-3">
            <input
              type="text"
              placeholder="flag.name"
              value={newFlag.name}
              onChange={(e) => setNewFlag({ ...newFlag, name: e.target.value })}
              className="px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] font-mono w-64 focus:outline-none focus:border-[var(--color-accent)]"
            />
            <input
              type="text"
              placeholder="Short description"
              value={newFlag.description}
              onChange={(e) => setNewFlag({ ...newFlag, description: e.target.value })}
              className="px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] flex-1 max-w-md focus:outline-none focus:border-[var(--color-accent)]"
            />
            <Button
              onClick={() => create.mutate({ name: newFlag.name, description: newFlag.description })}
              disabled={!newFlag.name || create.isPending}
            >
              <LuPlus className="w-4 h-4" />
              Create
            </Button>
          </div>
        </Card>

        <Card>
          <div className="divide-y divide-[var(--color-border)]">
            {list.data?.flags.length === 0 && (
              <p className="px-5 py-6 text-sm text-[var(--color-text-muted)]">No flags yet.</p>
            )}
            {list.data?.flags.map((f) => (
              <div key={f.id} className="px-5 py-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-mono text-[var(--color-text)]">{f.name}</p>
                  {f.description && (
                    <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5 truncate">
                      {f.description}
                    </p>
                  )}
                  <p className="text-[10px] text-[var(--color-text-subtle)] mt-1">
                    {f.updatedBy ? `by ${f.updatedBy} · ` : ""}
                    {new Date(f.updatedAt).toLocaleString()}
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <span className="relative inline-block w-9 h-5">
                    <input
                      type="checkbox"
                      checked={f.enabled}
                      onChange={(e) => update.mutate({ name: f.name, body: { enabled: e.target.checked } })}
                      className="peer sr-only"
                    />
                    <span className="absolute inset-0 rounded-full bg-[var(--color-elevated)] border border-[var(--color-border)] peer-checked:bg-[rgba(139,92,246,0.5)] peer-checked:border-[var(--color-accent)] transition-colors" />
                    <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-[var(--color-text-muted)] peer-checked:translate-x-4 peer-checked:bg-white transition-transform" />
                  </span>
                </label>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}
